<template>
  <div class="investment" data-testid="investment">
    <div class="investment__header">
      <Row
        class="investment__header--row"
        :has-line="false"
        :chevron="!hasBalance"
        @click="redirectToDeposit"
      >
        <template #top-left>
          <BaseText size="p4" :color="NebraskaColors.elementSecondary">
            Saldo em conta
          </BaseText>
        </template>
        <template #top-right>
          <BaseText
            size="p4"
            :color="NebraskaColors.elementPrimary"
            :class="headerValueClass"
            @click="redirectToDeposit"
          >
            {{ balanceValue }}
          </BaseText>
        </template>
      </Row>
    </div>
    <div class="investment__content">
      <HeadingDisplay
        class="investment__heading"
        title="Defina o valor a investir"
        :subtitle="investmentDetails.portfolioName"
      />
      <SingleInputMoney
        class="investment__single-input-money"
        :shortcut-value="investmentDetails.buttons"
        has-shortcut
        :status="status"
        :helper-text="helperText"
        @change="onChangeValue"
      />
      <ListDisplay
        :has-divider="true"
        title="Investimento mínimo inicial"
        :value="investmentDetails.minimumInvestment.formatted"
      />
      <ListDisplay
        :has-divider="true"
        title="Saldo atual da carteira"
        :value="investmentDetails.portfolioBalance.formatted"
      />
      <ListDisplay
        :has-divider="false"
        title="Total após investimento"
        :value="totalAfterInvestment"
      />
      <ButtonContainer class="investment__button-container">
        <template #button-primary>
          <ButtonPrimary
            label="Continuar"
            size="large"
            :disabled="isDisabled"
            @click="investClick"
          />
        </template>
      </ButtonContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, Ref } from 'vue';

import {
  HeadingDisplay,
  BaseText,
  ListDisplay,
  ButtonContainer,
  ButtonPrimary,
  Row
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { SingleInputMoney } from '@/modules/common/components/SingleInputMoney';
import { useRouter } from 'vue-router/composables';

import { IInvestmentDetails } from '@/modules/transaction/services/variable-income';
import { moneyFormat } from '@/modules/common/helpers/currency';

const changeAmountValue = inject(
  'changeAmountValue',
  (value: number, formatted: string) => ({ value, formatted })
);

const investmentDetails = inject(
  'investmentDetails'
) as Ref<IInvestmentDetails>;

const router = useRouter();
const amountFormatted = ref('');
const amountValue = ref(0);

const isDisabled = computed(() => {
  return amountValue.value <= 0 || status.value === 'error';
});

const minimumInvestment = computed(() => {
  return investmentDetails.value.minimumInvestment.value;
});

const availableBalance = computed(() => {
  return investmentDetails.value.availableBalance.value;
});

const hasBalance = computed(() => {
  return investmentDetails.value.availableBalance.value > 0;
});

const totalAfterInvestment = computed(() => {
  return moneyFormat(
    investmentDetails.value.portfolioBalance.value + amountValue.value
  );
});

const headerValueClass = computed(() => {
  return hasBalance.value ? '' : 'investment-step__header--value';
});

const balanceValue = computed(() => {
  return hasBalance.value
    ? investmentDetails.value.availableBalance.formatted
    : 'Depositar';
});

function redirectToDeposit() {
  if (hasBalance.value) return;

  router.push({ name: 'deposit-pix' });
}

const status = computed(() => {
  return (amountValue.value < minimumInvestment.value &&
    amountValue.value !== 0) ||
    amountValue.value > availableBalance.value
    ? 'error'
    : '';
});

const helperText = computed(() => {
  if (amountValue.value < minimumInvestment.value && amountValue.value !== 0)
    return `O valor mínimo de investimento nesta carteira é ${investmentDetails.value.minimumInvestment.formatted}`;
  if (amountValue.value > availableBalance.value)
    return `Valor maior que o saldo disponível: ${investmentDetails.value.availableBalance.formatted}`;

  return '';
});

function investClick() {
  changeAmountValue(amountValue.value, amountFormatted.value);
}

function onChangeValue(data: { value: number; formatted: string }) {
  amountFormatted.value = data.formatted;
  amountValue.value = data.value;
}
</script>

<style lang="less" scoped>
.width() {
  max-width: 592px;
  width: 100%;
}

.investment {
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: @size-spacing-x1800;

  &__content {
    .width();
  }

  &__header {
    background-color: @background-tertiary;
    height: 64px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 @size-spacing-x500;
    width: 100%;

    &--row {
      .width();
      background-color: transparent;
      padding: 0;
    }

    &--value {
      &:hover {
        cursor: pointer;
      }
    }
  }

  &__heading {
    margin-top: @size-spacing-x1000;
  }

  &__single-input-money {
    margin-top: @size-spacing-x400;
    margin-bottom: @size-spacing-x800;
  }

  &__account-value {
    margin-top: @size-spacing-x400;
  }

  &__minimum-investment {
    margin-top: @size-spacing-x500;
  }

  .list-display {
    margin-bottom: @size-spacing-x500;
  }

  &__button-container {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
